
import { supabase } from "@/integrations/supabase/client";
import { CalendarConnection, CalendarSelection } from "@/types/calendar";

// Store the last refresh time and in-progress refreshes to avoid race conditions
let lastRefreshTime = 0;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const inProgressRefreshes = new Set<string>();

export const refreshCalendarTokens = async (userId: string): Promise<void> => {
  try {
    console.log("Checking calendar tokens for user:", userId);
    
    // First verify that the current user session is valid
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("Invalid user session, cannot refresh calendar tokens:", sessionError);
      return;
    }
    
    // Get all calendar connections for the user
    const { data: connections, error } = await supabase
      .from('calendar_connections')
      .select('*')
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error fetching calendar connections:", error);
      return;
    }
    
    if (!connections || connections.length === 0) {
      console.log("No calendar connections found for user");
      return;
    }
    
    console.log(`Found ${connections.length} calendar connections to check`);
    
    // Check each connection and refresh if needed
    for (const connection of connections) {
      // Skip if already refreshing this connection
      if (inProgressRefreshes.has(connection.id)) {
        console.log(`Skipping ${connection.id} - refresh already in progress`);
        continue;
      }
      
      // Transform the database row to match our CalendarConnection type
      const transformedConnection: CalendarConnection = {
        ...connection,
        selected_calendars: Array.isArray(connection.selected_calendars) 
          ? connection.selected_calendars.map((cal: any) => 
              typeof cal === 'string' 
                ? { id: cal, name: cal } 
                : cal as CalendarSelection
            )
          : []
      };
      
      await checkAndRefreshConnection(transformedConnection);
    }
    
    // Update last refresh time
    lastRefreshTime = Date.now();
    
  } catch (error) {
    console.error("Error refreshing calendar tokens:", error);
  }
};

export const refreshCalendarTokensIfNeeded = async (userId: string): Promise<void> => {
  const now = Date.now();
  
  // Only refresh if enough time has passed since last refresh
  if (now - lastRefreshTime < REFRESH_INTERVAL) {
    console.log("Skipping token refresh - too soon since last refresh");
    return;
  }
  
  await refreshCalendarTokens(userId);
};

export const refreshConnectionIfNeeded = async (connectionId: string, userId: string): Promise<boolean> => {
  try {
    console.log("Checking specific connection for refresh:", connectionId);
    
    // Skip if already refreshing this connection
    if (inProgressRefreshes.has(connectionId)) {
      console.log(`Connection ${connectionId} refresh already in progress`);
      return false;
    }
    
    // Verify session before proceeding
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("Invalid user session, cannot refresh connection:", sessionError);
      return false;
    }
    
    const { data: connection, error } = await supabase
      .from('calendar_connections')
      .select('*')
      .eq('id', connectionId)
      .eq('user_id', userId)
      .single();
      
    if (error || !connection) {
      console.error("Error fetching calendar connection:", error);
      return false;
    }
    
    const transformedConnection: CalendarConnection = {
      ...connection,
      selected_calendars: Array.isArray(connection.selected_calendars) 
        ? connection.selected_calendars.map((cal: any) => 
            typeof cal === 'string' 
              ? { id: cal, name: cal } 
              : cal as CalendarSelection
          )
        : []
    };
    
    const refreshed = await checkAndRefreshConnection(transformedConnection);
    return refreshed;
    
  } catch (error) {
    console.error("Error checking specific connection:", error);
    return false;
  }
};

const checkAndRefreshConnection = async (connection: CalendarConnection): Promise<boolean> => {
  try {
    // Mark this connection as being refreshed
    inProgressRefreshes.add(connection.id);
    
    const now = new Date();
    const expiresAt = new Date(connection.expires_at);
    
    // Check if token expires within the next 10 minutes
    const tenMinutesFromNow = new Date(now.getTime() + 10 * 60 * 1000);
    
    if (expiresAt <= tenMinutesFromNow) {
      console.log(`Refreshing token for ${connection.provider} connection:`, connection.id);
      
      let refreshed = false;
      if (connection.provider === 'google') {
        refreshed = await refreshGoogleToken(connection);
      } else if (connection.provider === 'outlook') {
        refreshed = await refreshOutlookToken(connection);
      }
      
      return refreshed;
    } else {
      console.log(`Token for ${connection.provider} connection ${connection.id} is still valid`);
      return false;
    }
  } catch (error) {
    console.error(`Error checking connection ${connection.id}:`, error);
    return false;
  } finally {
    // Always remove from in-progress set
    inProgressRefreshes.delete(connection.id);
  }
};

const refreshGoogleToken = async (connection: CalendarConnection): Promise<boolean> => {
  try {
    // Verify we have a valid session before making external API calls
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("Cannot refresh Google token - invalid session");
      return false;
    }

    const refreshResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || "",
        client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET || "",
        refresh_token: connection.refresh_token,
        grant_type: 'refresh_token',
      }).toString(),
    });

    const refreshData = await refreshResponse.json();

    if (refreshData.error) {
      console.error("Google token refresh error:", refreshData.error);
      return false;
    }

    // Update the connection with new token
    const newExpiresAt = new Date();
    newExpiresAt.setSeconds(newExpiresAt.getSeconds() + refreshData.expires_in);

    const { error: updateError } = await supabase
      .from('calendar_connections')
      .update({
        access_token: refreshData.access_token,
        expires_at: newExpiresAt.toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', connection.id);

    if (updateError) {
      console.error("Error updating Google token:", updateError);
      return false;
    } else {
      console.log("Google token refreshed successfully for connection:", connection.id);
      return true;
    }
  } catch (error) {
    console.error("Error refreshing Google token:", error);
    return false;
  }
};

const refreshOutlookToken = async (connection: CalendarConnection): Promise<boolean> => {
  try {
    // Verify we have a valid session before making external API calls
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("Cannot refresh Outlook token - invalid session");
      return false;
    }

    const refreshResponse = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: import.meta.env.VITE_OUTLOOK_CLIENT_ID || "",
        client_secret: import.meta.env.VITE_OUTLOOK_CLIENT_SECRET || "",
        grant_type: 'refresh_token',
        refresh_token: connection.refresh_token,
      }),
    });

    if (!refreshResponse.ok) {
      const errorData = await refreshResponse.json();
      console.error('Failed to refresh Outlook token:', errorData);
      return false;
    }

    const tokenData = await refreshResponse.json();
    
    // Update the connection with new tokens
    const newExpiresAt = new Date(Date.now() + (tokenData.expires_in * 1000)).toISOString();
    
    const { error: updateError } = await supabase
      .from('calendar_connections')
      .update({
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token || connection.refresh_token,
        expires_at: newExpiresAt,
        updated_at: new Date().toISOString()
      })
      .eq('id', connection.id);

    if (updateError) {
      console.error('Failed to update Outlook tokens:', updateError);
      return false;
    } else {
      console.log("Outlook token refreshed successfully for connection:", connection.id);
      return true;
    }
  } catch (error) {
    console.error("Error refreshing Outlook token:", error);
    return false;
  }
};
